import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Paper, Grid, Chip } from '@mui/material';
import { PlayArrow, Refresh } from '@mui/icons-material';
import { GlassContainer } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';

const GRID_SIZE = 9;
const DISK_TYPES = [
  { name: 'Bad Sector', color: '#ff4444', points: 10 },
  { name: 'Corrupted', color: '#ff8800', points: 15 },
  { name: 'Failing', color: '#ff0066', points: 25 },
  { name: 'RAID Error', color: '#9900ff', points: 50 }
];

const DiskWhackAMole = () => {
  const { t } = useTranslation();
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [activeDisks, setActiveDisks] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [missedCount, setMissedCount] = useState(0);

  useEffect(() => {
    const savedHighScore = localStorage.getItem('diskWhackHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  const startGame = () => {
    setScore(0);
    setTimeLeft(30);
    setActiveDisks([]);
    setGameOver(false);
    setMissedCount(0);
    setGameStarted(true);
  };

  const whackDisk = useCallback((index) => {
    if (gameOver) return;

    const disk = activeDisks.find(d => d.index === index);
    if (disk) {
      setScore(prev => prev + disk.points);
      setActiveDisks(prev => prev.filter(d => d.index !== index));
    }
  }, [activeDisks, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (timeLeft <= 0) {
      setGameOver(true);
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('diskWhackHighScore', score);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, gameStarted, gameOver, score, highScore]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const spawnInterval = setInterval(() => {
      setActiveDisks(prev => {
        const newDisks = [...prev];
        
        // Remove expired disks (count as missed)
        newDisks.forEach(disk => {
          if (Date.now() > disk.expiresAt) {
            setMissedCount(m => m + 1);
          }
        });
        const filtered = newDisks.filter(d => Date.now() <= d.expiresAt);
        
        // Spawn new disk if we have space
        if (filtered.length < 4) {
          const availableSlots = [];
          for (let i = 0; i < GRID_SIZE; i++) {
            if (!filtered.some(d => d.index === i)) {
              availableSlots.push(i);
            }
          }
          
          if (availableSlots.length > 0) {
            const randomSlot = availableSlots[Math.floor(Math.random() * availableSlots.length)];
            const diskType = DISK_TYPES[Math.floor(Math.random() * DISK_TYPES.length)];
            filtered.push({
              index: randomSlot,
              ...diskType,
              expiresAt: Date.now() + 1500 - (Math.min(score, 200) / 10) // Gets faster as score increases
            });
          }
        }
        
        return filtered;
      });
    }, 600);

    return () => clearInterval(spawnInterval);
  }, [gameStarted, gameOver, score]);

  useEffect(() => {
    if (missedCount >= 10) {
      setGameOver(true);
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('diskWhackHighScore', score);
      }
    }
  }, [missedCount, score, highScore]);

  const getRandomEmoji = () => {
    const emojis = ['💾', '🖥️', '🔧', '⚠️', '🧟'];
    return emojis[Math.floor(Math.random() * emojis.length)];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff6b6b' }}>
            🔨 Disk Whack-a-Mole
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            Whack failing disks before they corrupt your data!
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip label={`Time: ${timeLeft}s`} sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', color: '#00d4ff' }} />
          <Chip label={`${t('games.diskWhack.score')}: ${score}`} sx={{ bgcolor: 'rgba(0, 255, 136, 0.2)', color: '#00ff88' }} />
          <Chip label={`${t('games.diskWhack.highScore')}: ${highScore}`} sx={{ bgcolor: 'rgba(255, 215, 0, 0.2)', color: '#ffd700' }} />
          <Chip label={`Missed: ${missedCount}/10`} sx={{ bgcolor: missedCount >= 7 ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)', color: '#ff4444' }} />
        </Box>
      </Box>

      {!gameStarted ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" sx={{ mb: 2, color: '#ff6b6b' }}>
            💾 Smack Those Bad Sectors!
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, maxWidth: 500, mx: 'auto' }}>
            Failing disks will pop up randomly. Click them fast to fix bad sectors!
            You have 30 seconds and can miss up to 10 disks.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            {DISK_TYPES.map((type) => (
              <Chip 
                key={type.name}
                label={`${type.name} (+${type.points})`}
                sx={{ 
                  bgcolor: `${type.color}30`,
                  border: `1px solid ${type.color}`,
                  color: type.color 
                }}
              />
            ))}
          </Box>

          <Button 
            variant="contained" 
            size="large"
            startIcon={<PlayArrow />}
            onClick={startGame}
            sx={{ 
              bgcolor: 'linear-gradient(135deg, #ff6b6b 0%, #ff4444 100%)',
              color: '#fff',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': {
                boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
              }
            }}
          >
            Start Whacking
          </Button>
        </GlassContainer>
      ) : gameOver ? (
        <GlassContainer sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h3" sx={{ mb: 2, color: '#ff4444' }}>
            {missedCount >= 10 ? '💥 Too Many Errors!' : '⏰ Time Up!'}
          </Typography>
          <Typography variant="h4" sx={{ mb: 4, color: '#00ff88' }}>
            {t('games.diskWhack.score')}: {score}
          </Typography>
          {score >= highScore && score > 0 && (
            <Typography variant="h5" sx={{ mb: 2, color: '#ffd700' }}>
              🏆 {t('games.diskWhack.newHighScore')}
            </Typography>
          )}
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
            {missedCount >= 10 ? `You missed ${missedCount} disks!` : `Disks whacked in 30 seconds!`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Refresh />}
              onClick={startGame}
              sx={{ 
                bgcolor: 'linear-gradient(135deg, #ff6b6b 0%, #ff4444 100%)',
                color: '#fff',
                fontWeight: 'bold',
                px: 4,
                '&:hover': {
                  boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
                }
              }}
            >
              Play Again
            </Button>
          </Box>
        </GlassContainer>
      ) : (
        <Grid container spacing={2}>
          {Array.from({ length: GRID_SIZE }).map((_, index) => {
            const disk = activeDisks.find(d => d.index === index);
            return (
              <Grid item xs={4} sm={4} md={4} key={index}>
                <Paper
                  onClick={() => whackDisk(index)}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    bgcolor: disk ? `${disk.color}40` : 'rgba(255, 255, 255, 0.03)',
                    border: disk ? `2px solid ${disk.color}` : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    fontSize: '2.5rem',
                    transition: 'all 0.1s ease',
                    '&:hover': {
                      bgcolor: disk ? `${disk.color}60` : 'rgba(255, 255, 255, 0.08)',
                      transform: 'scale(1.05)'
                    },
                    '&:active': {
                      transform: 'scale(0.95)'
                    }
                  }}
                >
                  {disk ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4">{getRandomEmoji()}</Typography>
                      <Typography variant="caption" sx={{ color: disk.color, fontWeight: 'bold' }}>
                        {disk.points}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.1)', fontSize: '1.5rem' }}>
                      ❌
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default DiskWhackAMole;
